/**
 * SponsorFooter — small "sponsored by X" line that renders under high-value
 * views (AI critique output, PDF, share previews). Zero visual weight when
 * there are no active sponsors.
 */
import React, { useEffect, useState } from "react";
import { fetchActiveSponsors, trackSponsorClick } from "@/lib/api";

export default function SponsorFooter({ context = "critique" }) {
  const [sponsors, setSponsors] = useState([]);
  useEffect(() => {
    fetchActiveSponsors()
      .then((d) => setSponsors(d.sponsors || []))
      .catch(() => setSponsors([]));
  }, []);

  if (sponsors.length === 0) return null;

  const handleClick = async (e, s) => {
    e.preventDefault();
    try {
      const { url } = await trackSponsorClick(s.sponsor_id);
      window.open(url || s.website_url, "_blank", "noopener,noreferrer");
    } catch {
      window.open(s.website_url, "_blank", "noopener,noreferrer");
    }
  };

  const label =
    context === "critique" ? "This critique made possible by"
    : context === "pdf" ? "This report brought to you by"
    : "Sponsored by";

  return (
    <div
      data-testid={`sponsor-footer-${context}`}
      className="mt-6 pt-4 border-t border-[#E5E2DC] flex items-center gap-3 flex-wrap text-xs text-[#5C6B62]"
    >
      <span className="uppercase tracking-[0.18em] text-[10px] font-semibold">
        {label}
      </span>
      {sponsors.map((s, i) => (
        <React.Fragment key={s.sponsor_id}>
          {i > 0 && <span aria-hidden className="text-[#C7C4BD]">·</span>}
          <a
            href={s.website_url}
            onClick={(e) => handleClick(e, s)}
            className="hover:text-[#2B5339] transition-colors inline-flex items-center gap-1.5"
            title={s.tagline || s.display_name}
          >
            {s.logo_url ? (
              <img
                src={s.logo_url}
                alt={s.display_name}
                className="h-4 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
              />
            ) : (
              <span className="font-medium">{s.display_name}</span>
            )}
          </a>
        </React.Fragment>
      ))}
    </div>
  );
}
